import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { faArrowDown, faArrowUp, faCheck, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SiteRouteHelmet from '../components/SiteRouteHelmet';
import { Link } from 'react-router-dom';
import './PricingPageExperiment.css';

type PricingFeature = {
  label: string;
  detail?: string;
  link?: { to: string; text: string };
};

type PricingCapacity = {
  liveBots: number;
  alwaysOnBots: number;
  backtestHours: number;
  revisions: number;
  aiCredits: number;
  support: string;
};

export type PricingPlan = {
  id: string;
  type?: string;
  name: string;
  price?: number;
  priceLabel?: string;
  originalPrice?: number;
  discountPercent?: number;
  tagline: string;
  bestFor?: string;
  cta: string;
  ctaLink?: string;
  productSlug?: string;
  planKey?: string;
  useCartCheckout?: boolean;
  billingPeriod?: 'monthly' | 'annual';
  pricePeriod?: 'month' | 'year';
  monthlyEquivalentPrice?: number;
  featured?: boolean;
  features: PricingFeature[];
  capacity?: PricingCapacity;
  /** Bootcamp cards only (built from the catalog in pricingEducationPlans.ts). */
  priceSuffix?: string;
  priceNote?: string;
  facts?: string[];
  checkoutClosedMessage?: string;
};

type PricingPageExperimentProps = {
  billingPeriod: 'monthly' | 'annual';
  billingPeriodControl?: React.ReactNode;
  businessPlan?: PricingPlan;
  corePlans: PricingPlan[];
  educationPlans: PricingPlan[];
  educationUnavailable?: boolean;
  formatPrice: (price?: number) => string;
  onPlanClick: (plan: PricingPlan) => void | Promise<void>;
  pendingPlanId: string | null;
};

type ComparisonRow = {
  label: string;
  shortLabel: string;
  value: (plan: PricingPlan) => React.ReactNode;
};

type ComparisonSection = {
  code: string;
  title: string;
  description: string;
  rows: ComparisonRow[];
};

const PLAN_DESCRIPTIONS: Record<string, string> = {
  light: 'Learn the workflow and run your first live bot.',
  starter: 'Build and deploy regularly with room to iterate.',
  pro: 'Scale active strategies with expert review each month.',
  'all-access': 'Run a high-volume workflow with priority support.',
};

const PLAN_CTA_LABELS: Record<string, string> = {
  light: 'Choose Light',
  starter: 'Choose Starter',
  pro: 'Choose Pro',
  'all-access': 'Choose All Access',
};

const SHARED_CAPABILITIES = [
  'AI strategy builder',
  'Broker and data connections',
  'SEC and public-market research',
  'Live deployment and monitoring',
  'Strategy marketplace',
];

function PendingLabel() {
  return (
    <>
      <span className="pricing-exp-spinner" aria-hidden="true" data-testid="pricing-cta-spinner" />
      <span>Loading...</span>
    </>
  );
}

function getPlanSwitcherLabel(plan: PricingPlan) {
  switch (plan.id) {
    case 'light':
      return 'Light';
    case 'starter':
      return 'Starter';
    case 'pro':
      return 'Pro';
    case 'all-access':
      return 'All Access';
    default:
      return plan.name;
  }
}

function getCheckoutFallbackHref(plan: PricingPlan): string | undefined {
  if (plan.useCartCheckout && plan.productSlug) {
    const params = new URLSearchParams({ product: plan.productSlug });
    if (plan.planKey) params.set('planKey', plan.planKey);
    return `/checkout?${params.toString()}`;
  }

  return plan.ctaLink;
}

function IncludedValue({ label = 'Included' }: { label?: string }) {
  return (
    <span className="pricing-exp-included">
      <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
      <span>{label}</span>
    </span>
  );
}

function formatRoundedCapacity(value: number) {
  return Math.round(value).toLocaleString();
}

function PlanPrice({
  plan,
  formatPrice,
}: {
  plan: PricingPlan;
  formatPrice: (price?: number) => string;
}) {
  const period =
    plan.priceSuffix?.replace(/^\//, '') ||
    (plan.type === 'course' ? 'one-time' : plan.pricePeriod || 'month');

  return (
    <div className="pricing-exp-price">
      <div className="pricing-exp-price__line">
        <strong>{plan.priceLabel || formatPrice(plan.price)}</strong>
        {!plan.priceLabel && <span>/ {period}</span>}
      </div>
      <div className="pricing-exp-price__offer">
        {plan.billingPeriod !== 'annual' && plan.originalPrice && (
          <span>Was {formatPrice(plan.originalPrice)}</span>
        )}
        {plan.billingPeriod !== 'annual' && plan.discountPercent && (
          <strong>Save {plan.discountPercent}%</strong>
        )}
      </div>
      {plan.billingPeriod === 'annual' && plan.monthlyEquivalentPrice && (
        <div className="pricing-exp-price__equivalent">
          {formatPrice(plan.monthlyEquivalentPrice)}/month · 2 months free
        </div>
      )}
    </div>
  );
}

function getPlanHighlights(plan: PricingPlan) {
  if (!plan.capacity) return [];

  const support =
    plan.id === 'pro'
      ? '2 support calls + Live Algo Lab'
      : plan.id === 'all-access'
        ? '4 support calls + priority help'
        : 'Community support';
  const creditAmount = formatRoundedCapacity(plan.capacity.aiCredits);
  const chat = `$${creditAmount} AI credits + ${plan.capacity.revisions.toLocaleString()} revisions / month`;

  return [
    chat,
    `${formatRoundedCapacity(plan.capacity.backtestHours)} backtesting hours / month`,
    `${plan.capacity.liveBots.toLocaleString()} bots total, including up to ${plan.capacity.alwaysOnBots.toLocaleString()} always-on`,
    support,
  ];
}

function PlanCard({
  formatPrice,
  isSelected,
  onPlanClick,
  pendingPlanId,
  plan,
}: {
  formatPrice: (price?: number) => string;
  isSelected: boolean;
  onPlanClick: (plan: PricingPlan) => void | Promise<void>;
  pendingPlanId: string | null;
  plan: PricingPlan;
}) {
  const isPending = pendingPlanId === plan.id;
  const checkoutFallbackHref = getCheckoutFallbackHref(plan);

  return (
    <article
      className={`pricing-exp-plan${plan.featured ? ' pricing-exp-plan--featured' : ''}${isSelected ? ' pricing-exp-plan--selected' : ''}`}
      id={`pricing-plan-${plan.id}`}
    >
      <div className="pricing-exp-plan__intro">
        <div className="pricing-exp-plan__title-row">
          <h2>{plan.name}</h2>
          {plan.featured && (
            <span className="pricing-exp-plan__popular">Recommended for most builders</span>
          )}
        </div>
        <p>{PLAN_DESCRIPTIONS[plan.id] || plan.tagline}</p>
      </div>

      <PlanPrice plan={plan} formatPrice={formatPrice} />

      <a
        className="pricing-exp-plan__cta"
        href={checkoutFallbackHref}
        role="button"
        onClick={(event) => {
          if (pendingPlanId) {
            event.preventDefault();
            return;
          }
          event.preventDefault();
          void onPlanClick(plan);
        }}
        aria-disabled={Boolean(pendingPlanId)}
        aria-busy={isPending}
      >
        {isPending ? <PendingLabel /> : PLAN_CTA_LABELS[plan.id] || plan.cta}
      </a>

      <div className="pricing-exp-plan__includes">
        <h3>Your monthly capacity</h3>
        <ul>
          {getPlanHighlights(plan).map((highlight) => (
            <li key={highlight}>
              <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
              <span>{highlight}</span>
            </li>
          ))}
        </ul>
      </div>

      <a className="pricing-exp-plan__all-features" href="#plan-comparison">
        <FontAwesomeIcon icon={faArrowDown} aria-hidden="true" />
        Compare capacity and support
      </a>
    </article>
  );
}

function PlanDeck({
  corePlans,
  formatPrice,
  onPlanClick,
  pendingPlanId,
}: {
  corePlans: PricingPlan[];
  formatPrice: (price?: number) => string;
  onPlanClick: (plan: PricingPlan) => void | Promise<void>;
  pendingPlanId: string | null;
}) {
  const recommendedPlan = corePlans.find((plan) => plan.featured) || corePlans[0];
  const [selectedPlanId, setSelectedPlanId] = useState(recommendedPlan?.id || '');

  useEffect(() => {
    if (!corePlans.some((plan) => plan.id === selectedPlanId)) {
      setSelectedPlanId(recommendedPlan?.id || corePlans[0]?.id || '');
    }
  }, [corePlans, recommendedPlan?.id, selectedPlanId]);

  return (
    <>
      <div className="pricing-exp-plan-switcher" aria-label="Choose a plan to review">
        {corePlans.map((plan) => (
          <button
            type="button"
            aria-pressed={selectedPlanId === plan.id}
            key={plan.id}
            onClick={() => setSelectedPlanId(plan.id)}
          >
            {getPlanSwitcherLabel(plan)}
          </button>
        ))}
      </div>

      <div className="pricing-exp-plans">
        {corePlans.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            isSelected={selectedPlanId === plan.id}
            formatPrice={formatPrice}
            onPlanClick={onPlanClick}
            pendingPlanId={pendingPlanId}
          />
        ))}
      </div>
    </>
  );
}

function SharedCapabilities() {
  return (
    <section className="pricing-exp-shared" aria-labelledby="pricing-shared-title">
      <header>
        <h2 id="pricing-shared-title">The full workflow is included</h2>
        <p>Plans change capacity, not what you can build.</p>
      </header>
      <ul>
        {SHARED_CAPABILITIES.map((capability) => (
          <li key={capability}>
            <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
            <span>{capability}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function buildComparisonSections(): ComparisonSection[] {
  return [
    {
      code: 'CAPACITY',
      title: 'AI and iteration',
      description: 'How much room you have to create and refine strategies each month.',
      rows: [
        {
          label: 'AI credits each month',
          shortLabel: 'AI credits',
          value: (plan) => {
            if (!plan.capacity) return 'Not listed';
            return `$${formatRoundedCapacity(plan.capacity.aiCredits)}`;
          },
        },
        {
          label: 'Strategy revisions each month',
          shortLabel: 'Revisions',
          value: (plan) => plan.capacity?.revisions.toLocaleString() || 'Not listed',
        },
      ],
    },
    {
      code: 'RUN',
      title: 'Build and run',
      description: 'How much historical testing and live deployment capacity you get.',
      rows: [
        {
          label: 'Backtesting each month',
          shortLabel: 'Backtesting',
          value: (plan) =>
            plan.capacity
              ? `${formatRoundedCapacity(plan.capacity.backtestHours)} hours`
              : 'Not listed',
        },
        {
          label: 'Total bots at one time',
          shortLabel: 'Total bots',
          value: (plan) => plan.capacity?.liveBots.toLocaleString() || 'Not listed',
        },
        {
          label: 'Always-on or high-duty bots',
          shortLabel: 'Always-on',
          value: (plan) => plan.capacity?.alwaysOnBots.toLocaleString() || 'Not listed',
        },
      ],
    },
    {
      code: 'SUPPORT',
      title: 'Expert support',
      description: 'Community help is included in every plan. Direct support starts with Pro.',
      rows: [
        {
          label: 'Support calls each month',
          shortLabel: 'Calls',
          value: (plan) =>
            plan.id === 'pro' ? '2' : plan.id === 'all-access' ? '4' : 'Not included',
        },
        {
          label: 'Live Algo Lab',
          shortLabel: 'Algo Lab',
          value: (plan) =>
            ['pro', 'all-access'].includes(plan.id) ? <IncludedValue /> : 'Not included',
        },
        {
          label: 'Priority help',
          shortLabel: 'Priority',
          value: (plan) => (plan.id === 'all-access' ? <IncludedValue /> : 'Not included'),
        },
      ],
    },
  ];
}

function ComparisonHeader({
  formatPrice,
  onPlanClick,
  pendingPlanId,
  plans,
}: {
  formatPrice: (price?: number) => string;
  onPlanClick: (plan: PricingPlan) => void | Promise<void>;
  pendingPlanId: string | null;
  plans: PricingPlan[];
}) {
  const barRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [isPinned, setIsPinned] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    let animationFrame = 0;

    const updatePinnedState = () => {
      animationFrame = 0;
      const bar = barRef.current;
      const sentinel = sentinelRef.current;
      if (!bar || !sentinel) return;

      const headerHeight =
        Number.parseFloat(
          window.getComputedStyle(bar).getPropertyValue('--pricing-app-header-height'),
        ) || 85;
      const isDesktop = window.matchMedia?.('(min-width: 901px)')?.matches ?? true;
      const barRect = bar.getBoundingClientRect();
      const zoneRect = bar.parentElement?.getBoundingClientRect();
      const hasMeasuredZone = Boolean(zoneRect && zoneRect.height > 0);
      const hasReachedStickyPoint =
        isDesktop && hasMeasuredZone && sentinel.getBoundingClientRect().top <= headerHeight;
      const nextExiting = Boolean(
        hasReachedStickyPoint && zoneRect && zoneRect.bottom <= barRect.height + 1,
      );
      const nextPinned = hasReachedStickyPoint && !nextExiting;

      setIsExiting((current) => (current === nextExiting ? current : nextExiting));
      setIsPinned((current) => (current === nextPinned ? current : nextPinned));
    };

    const schedulePinnedStateUpdate = () => {
      if (!animationFrame) {
        animationFrame = window.requestAnimationFrame(updatePinnedState);
      }
    };

    updatePinnedState();
    window.addEventListener('resize', schedulePinnedStateUpdate, {
      passive: true,
    });
    window.addEventListener('scroll', schedulePinnedStateUpdate, {
      passive: true,
    });

    return () => {
      window.removeEventListener('resize', schedulePinnedStateUpdate);
      window.removeEventListener('scroll', schedulePinnedStateUpdate);
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
    };
  }, []);

  useEffect(() => {
    document.body.classList.toggle('pricing-comparison-pinned', isPinned);

    return () => {
      document.body.classList.remove('pricing-comparison-pinned');
    };
  }, [isPinned]);

  return (
    <>
      <div className="pricing-exp-compare-sentinel" ref={sentinelRef} aria-hidden="true" />
      <div
        className={`pricing-exp-compare-bar${isPinned ? ' pricing-exp-compare-bar--pinned' : ''}${isExiting ? ' pricing-exp-compare-bar--exiting' : ''}`}
        id="pricing-comparison-header"
        ref={barRef}
      >
        <div className="pricing-exp-shell pricing-exp-compare-bar__grid">
          <div className="pricing-exp-compare-bar__intro">
            <strong>Compare plans</strong>
            <a href="#pricing-plans">
              <FontAwesomeIcon icon={faArrowUp} aria-hidden="true" />
              Plans
            </a>
          </div>
          {plans.map((plan) => {
            const isPending = pendingPlanId === plan.id;
            return (
              <div
                className={`pricing-exp-compare-bar__plan${plan.featured ? ' pricing-exp-compare-bar__plan--featured' : ''}`}
                key={plan.id}
              >
                <div>
                  <strong>{plan.name}</strong>
                  <span>
                    {plan.priceLabel ||
                      `${formatPrice(plan.price)}/${plan.pricePeriod === 'year' ? 'yr' : 'mo'}`}
                  </span>
                </div>
                <button
                  type="button"
                  aria-label={`Choose ${plan.name}`}
                  onClick={() => onPlanClick(plan)}
                  disabled={Boolean(pendingPlanId)}
                  aria-busy={isPending}
                >
                  {isPending ? <PendingLabel /> : 'Choose'}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

function ComparisonSectionDesktop({
  plans,
  section,
}: {
  plans: PricingPlan[];
  section: ComparisonSection;
}) {
  return (
    <section
      className="pricing-exp-comparison-section"
      aria-labelledby={`pricing-comparison-${section.code}`}
    >
      <header>
        <h2 id={`pricing-comparison-${section.code}`}>{section.title}</h2>
        <p>{section.description}</p>
      </header>
      <table aria-label={`${section.title} plan comparison`}>
        <tbody>
          {section.rows.map((row) => (
            <tr key={row.label}>
              <th scope="row">{row.label}</th>
              {plans.map((plan) => (
                <td
                  className={plan.featured ? 'pricing-exp-comparison-cell--featured' : undefined}
                  key={plan.id}
                >
                  {row.value(plan)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function ComparisonSectionMobile({
  isOpen,
  onToggle,
  plans,
  section,
}: {
  isOpen: boolean;
  onToggle: () => void;
  plans: PricingPlan[];
  section: ComparisonSection;
}) {
  return (
    <details className="pricing-exp-comparison-mobile" open={isOpen}>
      <summary
        onClick={(event) => {
          event.preventDefault();
          onToggle();
        }}
      >
        <span>
          <strong>{section.title}</strong>
          <small>
            {section.rows.length} {section.rows.length === 1 ? 'difference' : 'differences'}
          </small>
        </span>
        <FontAwesomeIcon
          className="pricing-exp-comparison-mobile__chevron"
          icon={faChevronDown}
          aria-hidden="true"
        />
      </summary>
      <div className="pricing-exp-comparison-mobile__content">
        <p>{section.description}</p>
        <div className="pricing-exp-comparison-mobile__table-wrap">
          <table
            className="pricing-exp-comparison-mobile__table"
            aria-label={`${section.title} mobile plan comparison`}
          >
            <thead>
              <tr>
                <th scope="col">Plan</th>
                {section.rows.map((row) => (
                  <th scope="col" key={row.label}>
                    {row.shortLabel}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {plans.map((plan) => (
                <tr
                  className={
                    plan.featured ? 'pricing-exp-comparison-mobile__plan--featured' : undefined
                  }
                  key={plan.id}
                >
                  <th scope="row">{plan.name}</th>
                  {section.rows.map((row) => (
                    <td key={row.label}>
                      <span className="pricing-exp-comparison-mobile__label" aria-hidden="true">
                        {row.shortLabel}
                      </span>
                      <span>{row.value(plan)}</span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </details>
  );
}

function PlanComparison({ plans }: { plans: PricingPlan[] }) {
  const sections = buildComparisonSections();
  const [openSectionCode, setOpenSectionCode] = useState<string | null>(sections[0]?.code || null);

  return (
    <section
      className="pricing-exp-shell pricing-exp-comparison"
      id="plan-comparison"
      aria-labelledby="pricing-comparison-title"
    >
      <header className="pricing-exp-comparison__intro">
        <h2 id="pricing-comparison-title">Compare capacity and support</h2>
        <p>Everything else is included in every subscription.</p>
      </header>
      <div className="pricing-exp-comparison__desktop">
        {sections.map((section) => (
          <ComparisonSectionDesktop key={section.code} plans={plans} section={section} />
        ))}
      </div>
      <div className="pricing-exp-comparison__mobile">
        {sections.map((section) => (
          <ComparisonSectionMobile
            isOpen={openSectionCode === section.code}
            key={section.code}
            onToggle={() =>
              setOpenSectionCode((current) => (current === section.code ? null : section.code))
            }
            plans={plans}
            section={section}
          />
        ))}
      </div>
    </section>
  );
}

function TrainingSection({
  educationPlans,
  educationUnavailable,
  formatPrice,
  onPlanClick,
  pendingPlanId,
}: {
  educationPlans: PricingPlan[];
  educationUnavailable?: boolean;
  formatPrice: (price?: number) => string;
  onPlanClick: (plan: PricingPlan) => void | Promise<void>;
  pendingPlanId: string | null;
}) {
  if (!educationPlans.length && !educationUnavailable) return null;

  // AI Bootcamp leads the page (Rob, 2026-10-14). Prices, payment plans and
  // facts come only from the checkout catalog.
  return (
    <section
      className="pricing-exp-training pricing-exp-training--lead"
      id="pricing-training"
      aria-labelledby="pricing-training-title"
    >
      <header>
        <h2 id="pricing-training-title">AI Bootcamp</h2>
        <p>
          A one-time purchase, separate from your monthly plan. Learn live with the class, or start
          the recordings today.
        </p>
      </header>
      {educationUnavailable ? (
        <p className="pricing-exp-training__unavailable" role="alert">
          Bootcamp pricing is unavailable right now. Please refresh and try again.
        </p>
      ) : (
        <div className="pricing-exp-training__list">
          {educationPlans.map((plan) => {
            const isPending = pendingPlanId === plan.id;
            const closed = Boolean(plan.checkoutClosedMessage);
            return (
              <article key={plan.id} id={`pricing-plan-${plan.id}`}>
                <div className="pricing-exp-training__copy">
                  <h3>{plan.name}</h3>
                  <p>{plan.tagline}</p>
                  {plan.priceNote && <p>{plan.priceNote}</p>}
                  {plan.facts && plan.facts.length > 0 && (
                    <ul className="pricing-exp-training__facts">
                      {plan.facts.map((fact) => (
                        <li key={fact}>
                          <FontAwesomeIcon icon={faCheck} aria-hidden="true" />
                          {fact}
                        </li>
                      ))}
                    </ul>
                  )}
                  {closed && <p role="status">{plan.checkoutClosedMessage}</p>}
                </div>
                <PlanPrice plan={plan} formatPrice={formatPrice} />
                <button
                  type="button"
                  onClick={() => onPlanClick(plan)}
                  disabled={Boolean(pendingPlanId) || closed}
                  aria-busy={isPending}
                >
                  {isPending ? <PendingLabel /> : plan.cta}
                </button>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

const FAQS = [
  {
    question: 'Which plan should I choose?',
    answer:
      'Light is for learning and a first live bot. Starter fits most builders who test and deploy regularly. Pro adds expert support and more capacity. All Access is for the highest-volume workflows.',
  },
  {
    question: 'What changes between plans?',
    answer:
      'AI chat, strategy revisions, backtesting hours, live bot capacity, and direct support increase as you move up. The core build, research, broker, deployment, and monitoring workflow is included throughout.',
  },
  {
    question: 'Which plans include Live Algo Lab?',
    answer:
      'Pro and All Access include Live Algo Lab. Pro includes two support calls each month, while All Access includes four and priority help.',
  },
  {
    question: 'Are premium market data fees included?',
    answer:
      'Broker and market data connections are included. Some premium data sources require their own subscription.',
  },
  {
    question: 'Can I get a refund?',
    answer:
      'The Bootcamp guarantee applies only to eligible AI Bootcamp purchases. BotSpot subscriptions are not covered by that guarantee.',
  },
  {
    question: 'Is AI Bootcamp included?',
    answer:
      'No. Live and self-paced Bootcamp are separate one-time purchases. Their prices are listed at the top of this page.',
  },
];

function FAQItem({
  answer,
  isOpen,
  onToggle,
  question,
}: {
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
  question: string;
}) {
  return (
    <details open={isOpen}>
      <summary
        onClick={(event) => {
          event.preventDefault();
          onToggle();
        }}
      >
        <span>{question}</span>
        <FontAwesomeIcon icon={faChevronDown} aria-hidden="true" />
      </summary>
      <p>{answer}</p>
    </details>
  );
}

function FAQSection({ businessPlan }: { businessPlan?: PricingPlan }) {
  const [openQuestion, setOpenQuestion] = useState<string | null>(FAQS[0].question);

  return (
    <section className="pricing-exp-shell pricing-exp-faq" aria-labelledby="pricing-faq-title">
      <div className="pricing-exp-faq__intro">
        <h2 id="pricing-faq-title">Questions, answered</h2>
        <p>Need help choosing? Talk with the BotSpot team.</p>
        <Link to={businessPlan?.ctaLink || '/enterprise'}>Contact us</Link>
      </div>
      <div className="pricing-exp-faq__list">
        {FAQS.map((faq) => (
          <FAQItem
            answer={faq.answer}
            isOpen={openQuestion === faq.question}
            key={faq.question}
            onToggle={() =>
              setOpenQuestion((current) => (current === faq.question ? null : faq.question))
            }
            question={faq.question}
          />
        ))}
      </div>
    </section>
  );
}

function FinalCTA({
  formatPrice,
  onPlanClick,
  pendingPlanId,
  plan,
}: {
  formatPrice: (price?: number) => string;
  onPlanClick: (plan: PricingPlan) => void | Promise<void>;
  pendingPlanId: string | null;
  plan: PricingPlan;
}) {
  const isPending = pendingPlanId === plan.id;

  return (
    <section className="pricing-exp-shell pricing-exp-final" aria-labelledby="pricing-final-title">
      <div>
        <h2 id="pricing-final-title">Ready to build and run your strategy?</h2>
        <p>{plan.name} gives most builders enough room to test, iterate, and deploy regularly.</p>
      </div>
      <div className="pricing-exp-final__action">
        <div className="pricing-exp-final__buttons">
          <button
            type="button"
            onClick={() => onPlanClick(plan)}
            disabled={Boolean(pendingPlanId)}
            aria-busy={isPending}
          >
            {isPending ? (
              <PendingLabel />
            ) : (
              `Choose ${plan.name} · ${formatPrice(plan.price)}/${plan.pricePeriod || 'month'}`
            )}
          </button>
          <a href="#plan-comparison">Compare all plans</a>
        </div>
        <small>Manage your subscription from your account</small>
      </div>
    </section>
  );
}

export default function PricingPageExperiment({
  billingPeriod,
  billingPeriodControl,
  businessPlan,
  corePlans,
  educationPlans,
  educationUnavailable,
  formatPrice,
  onPlanClick,
  pendingPlanId,
}: PricingPageExperimentProps) {
  const recommendedPlan = corePlans.find((plan) => plan.featured) || corePlans[0];

  useLayoutEffect(() => {
    document.body.classList.add('pricing-redesign-active');

    return () => {
      document.body.classList.remove('pricing-redesign-active');
    };
  }, []);

  return (
    <div className="pricing-exp">
      <SiteRouteHelmet path="/pricing" />

      <section className="pricing-exp-top" id="pricing-plans" aria-labelledby="pricing-title">
        <div className="pricing-exp-shell">
          <header className="pricing-exp-hero">
            <h1 id="pricing-title">
              <span>Learn it in the Bootcamp. </span>
              <em>Run it with a plan.</em>
            </h1>
            <p>
              Start with the AI Bootcamp, or pick a monthly plan. Every plan includes the full
              BotSpot workflow.
            </p>
          </header>

          <TrainingSection
            educationPlans={educationPlans}
            educationUnavailable={educationUnavailable}
            formatPrice={formatPrice}
            onPlanClick={onPlanClick}
            pendingPlanId={pendingPlanId}
          />

          <header className="pricing-exp-plans-header">
            <h2>Monthly plans</h2>
            <p>Compare monthly capacity and support.</p>
          </header>

          {billingPeriodControl}

          <PlanDeck
            corePlans={corePlans}
            formatPrice={formatPrice}
            onPlanClick={onPlanClick}
            pendingPlanId={pendingPlanId}
          />

          <SharedCapabilities />

          <p className="pricing-exp-billing-note">
            {billingPeriod === 'annual' ? 'Billed yearly' : 'Billed monthly'}
            <span aria-hidden="true">·</span> Manage your subscription from your account
          </p>
        </div>
      </section>

      <div className="pricing-exp-comparison-zone">
        <ComparisonHeader
          formatPrice={formatPrice}
          onPlanClick={onPlanClick}
          pendingPlanId={pendingPlanId}
          plans={corePlans}
        />

        <PlanComparison plans={corePlans} />
      </div>

      {businessPlan && (
        <section className="pricing-exp-sales" aria-labelledby="pricing-sales-title">
          <div>
            <h2 id="pricing-sales-title">Need a custom plan?</h2>
            <p>
              Get custom capacity, connectors, security reviews, and support terms for your team.
            </p>
          </div>
          <Link to={businessPlan.ctaLink || '/enterprise'}>{businessPlan.cta}</Link>
        </section>
      )}

      <section className="pricing-exp-bottom">
        <FAQSection businessPlan={businessPlan} />

        {recommendedPlan && (
          <FinalCTA
            formatPrice={formatPrice}
            onPlanClick={onPlanClick}
            pendingPlanId={pendingPlanId}
            plan={recommendedPlan}
          />
        )}

        <p className="pricing-exp-shell pricing-exp-risk">
          Trading involves risk. BotSpot helps you build and test strategies, but no plan promises
          investment results.
        </p>
      </section>
    </div>
  );
}
